import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { Background, backgrounds as defaultBackgrounds } from 'shared/constants/backgrounds';
import { MAX_SOURCE_BYTES } from 'shared/utils/image/image-messages';
import { FileSize } from 'ud-form/components/file-size';
import { IMAGE_ACCEPT, UDDropzone, useDropzoneField } from 'ud-form/components/image-dropzone';
import UDFormFieldWrapper from 'ud-form/wrapper';
import { LogoPreview } from './component';
import { logoValueAdapter, readLogo, writeLogo } from './domain/logo-value';
import { UDFormLogoFieldProps } from './types';

/**
 * Поле «логотип с подложкой».
 *
 * Это не режим дропзоны, хотя раньше было им: `CoverDropzone` считался третьим
 * вариантом дропзоны, а на деле отличался не раскладкой, а тем, что хранит в
 * форме — картинку вместе с цветом подложки под ней. Ради этого он подменял
 * дропзоне обработчик выбора файла и рисковал остаться без сжатия и объяснений.
 *
 * Теперь дропзона занимается картинками, а поле — своим значением: как его
 * прочитать и что записать обратно, описывает `logoValueAdapter`.
 */
export const UDFormLogoField = (props: UDFormLogoFieldProps) => {
  const {
    name,
    containerProps,
    label,
    labelProps,
    required,
    errorVisible,
    tooltip,
    tooltipProps,
    backgrounds = defaultBackgrounds,
    accept = IMAGE_ACCEPT,
    maxSize = MAX_SOURCE_BYTES,
    compression,
    crop,
    cropAspect,
    upscale,
    disabled,
    title,
    subtitle,
    buttonText,
    onButtonClick,
    className,
  } = props;

  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const value = values[name];
  const logo = useMemo(() => readLogo(value), [value]);

  const {
    previews,
    rootProps,
    inputProps,
    isDragActive,
    isFocused,
    isProcessing,
    isDisabled,
    cropFile,
    confirmCrop,
    skipCrop,
    cancelCrop,
    upscale: upscaleView,
  } = useDropzoneField({
    name,
    accept,
    maxSize,
    compression,
    crop,
    cropAspect,
    upscale,
    disabled,
    adapter: logoValueAdapter,
  });

  const onChangeBackground = (background: Background) => {
    setFieldValue(name, writeLogo(value, { background }));
  };

  // Адрес берётся из превью, а не создаётся здесь: там он заводится один раз на
  // файл и отзывается, когда картинку убрали или поле размонтировали.
  const img = previews[0]?.url;

  return (
    <UDFormFieldWrapper
      name={name}
      containerProps={containerProps}
      label={label}
      labelProps={labelProps}
      required={required}
      errorVisible={errorVisible}
      tooltip={tooltip}
      tooltipProps={tooltipProps}
    >
      {({ meta }) => (
        <UDDropzone
          previews={previews}
          rootProps={rootProps}
          inputProps={inputProps}
          accept={accept}
          maxSize={maxSize}
          preview="none"
          isDragActive={isDragActive}
          isFocused={isFocused}
          isProcessing={isProcessing}
          isDisabled={isDisabled}
          cropFile={cropFile}
          cropAspect={cropAspect}
          onCrop={confirmCrop}
          onCropSkip={skipCrop}
          onCropCancel={cancelCrop}
          upscale={upscaleView}
          hasError={(meta.touched || Boolean(meta.value)) && Boolean(meta.error)}
          title={title}
          subtitle={subtitle}
          buttonText={buttonText}
          onButtonClick={onButtonClick}
          className={className}
        >
          {img && (
            <>
              <LogoPreview
                img={img}
                background={logo.background ?? backgrounds[0]}
                backgrounds={backgrounds}
                onChangeBackground={onChangeBackground}
              />
              <FileSize value={logo.img} />
            </>
          )}
        </UDDropzone>
      )}
    </UDFormFieldWrapper>
  );
};

export default UDFormLogoField;
